import axios from 'axios';

// En desarrollo local usa localhost, en producción usa la URL relativa
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const api = axios.create({ baseURL: `${API_BASE}/api`, headers: { 'Content-Type': 'application/json' } });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cofres_user_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use((r) => r, (error) => {
  if (error.response?.status === 401 && !window.location.pathname.startsWith('/admin')) {
    localStorage.removeItem('cofres_user_token');
    localStorage.removeItem('cofres_user');
  }
  return Promise.reject(error);
});

// Auth
export const registerUser = (data) => api.post('/auth/register', data);
export const verifyRegistration = (phone, code) => api.post('/auth/verify', { phone, code });
export const resendCode = (phone) => api.post('/auth/resend-code', { phone });
export const userLogin = (phone, password) => api.post('/auth/login', { phone, password });
export const verifyLogin = (phone, code) => api.post('/auth/verify-login', { phone, code });
export const getMe = () => api.get('/auth/me');
export const checkReferralCode = (code) => api.get(`/auth/check-referral/${code}`);

// Public
export const getActiveRaffles = () => api.get('/raffles/active');
export const getRaffleHistory = () => api.get('/raffles/history');
export const getRaffleDetail = (id) => api.get(`/raffle/${id}`);

// Protected (raffle_id required)
export const getChests = (raffleId, page = 1, perPage = 100) => api.get(`/chests?raffle_id=${raffleId}&page=${page}&per_page=${perPage}`);
export const openChest = (chestNumber, raffleId) => api.post(`/chests/${chestNumber}/open?raffle_id=${raffleId}`);
export const getRandomAvailableChest = (raffleId) => api.get(`/chests/random-available?raffle_id=${raffleId}`);
export const searchChest = (number, raffleId) => api.get(`/chests/search/${number}?raffle_id=${raffleId}`);
export const addToCart = (raffleId, chestNumber) => api.post('/cart/add', { raffle_id: raffleId, chest_number: chestNumber });
export const removeFromCart = (raffleId, chestNumber) => api.post('/cart/remove', { raffle_id: raffleId, chest_number: chestNumber });
export const getCart = (raffleId) => api.get(`/cart?raffle_id=${raffleId}`);
export const createPurchase = (raffleId) => api.post('/purchase', { raffle_id: raffleId });

// Mi Red
export const getUserNetwork = (raffleId) => api.get(`/user/network${raffleId ? '?raffle_id=' + raffleId : ''}`);
export const getUserPurchases = (raffleId) => api.get(`/user/purchases${raffleId ? '?raffle_id=' + raffleId : ''}`);

// Admin
export const adminLogin = (email, password) => api.post('/admin/login', { email, password });
export const adminGetRaffles = (token) => api.get(`/admin/raffles?authorization=Bearer ${token}`);
export const adminCreateRaffle = (data, token) => api.post(`/admin/raffles?authorization=Bearer ${token}`, data);
export const adminUpdateRaffleStatus = (raffleId, status, token) => api.put(`/admin/raffles/${raffleId}/status?authorization=Bearer ${token}`, { status });
export const adminToggleFeatured = (raffleId, token) => api.put(`/admin/raffles/${raffleId}/featured?authorization=Bearer ${token}`);
export const adminGetPresetConfig = (templateType, token) => api.get(`/admin/raffles/preset-config/${templateType}?authorization=Bearer ${token}`);
export const adminGetRaffleDetail = (raffleId, token) => api.get(`/admin/raffles/${raffleId}/detail?authorization=Bearer ${token}`);

// Twilio / Messages
export const adminGetMessages = (page, perPage, direction, token) => {
  let url = `/admin/messages?page=${page}&per_page=${perPage}&authorization=Bearer ${token}`;
  if (direction) url += `&direction=${direction}`;
  return api.get(url);
};
export const adminSendSms = (toNumber, body, token) => api.post(`/admin/messages/send?authorization=Bearer ${token}`, { to_number: toNumber, body });
export const adminGetMessagesStats = (token) => api.get(`/admin/messages/stats?authorization=Bearer ${token}`);
export const adminGetChests = (raffleId, page, perPage, state, search, token) =>
  api.get(`/admin/chests?raffle_id=${raffleId}&page=${page}&per_page=${perPage}${state ? '&state=' + state : ''}${search ? '&search=' + search : ''}&authorization=Bearer ${token}`);
export const adminChangeChestState = (raffleId, chestNumber, state, token) =>
  api.put(`/admin/chests/${chestNumber}/state?raffle_id=${raffleId}&authorization=Bearer ${token}`, { state });
export const adminGetUsers = (page, perPage, token, q = '', status = '') => {
  let url = `/admin/users?page=${page}&per_page=${perPage}&authorization=Bearer ${token}`;
  if (q) url += `&q=${encodeURIComponent(q)}`;
  if (status) url += `&status=${status}`;
  return api.get(url);
};
export const adminBlockUser = (userId, blocked, reason, token) => 
  api.post(`/admin/users/${userId}/block?authorization=Bearer ${token}`, { blocked, reason });
export const adminDeleteUser = (userId, token) => 
  api.delete(`/admin/users/${userId}?authorization=Bearer ${token}`);
export const adminGetPurchases = (page, perPage, status, token, raffleId) =>
  api.get(`/admin/purchases?page=${page}&per_page=${perPage}${status ? '&status=' + status : ''}${raffleId ? '&raffle_id=' + raffleId : ''}&authorization=Bearer ${token}`);
export const adminUpdatePurchaseStatus = (purchaseId, status, token) => api.put(`/admin/purchases/${purchaseId}/status?status=${status}&authorization=Bearer ${token}`);
export const adminExportPurchases = (token, raffleId) => api.get(`/admin/export/purchases?authorization=Bearer ${token}${raffleId ? '&raffle_id=' + raffleId : ''}`, { responseType: 'blob' });
export const adminNewRaffle = (token) => api.post(`/admin/raffle/new?authorization=Bearer ${token}`, { archive_current: true });
export const adminGetStats = (token, raffleId) => api.get(`/admin/stats?authorization=Bearer ${token}${raffleId ? '&raffle_id=' + raffleId : ''}`);
export const adminChangePassword = (cur, nw, token) => api.put(`/admin/change-password?authorization=Bearer ${token}`, { current_password: cur, new_password: nw });
export const adminSearchWinner = (chestNumber, raffleId, token) => api.get(`/admin/winner/${chestNumber}?raffle_id=${raffleId}&authorization=Bearer ${token}`);
export const adminMarkPaid = (data, token) => api.post(`/admin/winner/mark-paid?authorization=Bearer ${token}`, data);
export const adminExportWinner = (chestNumber, raffleId, token) => api.get(`/admin/winner/${chestNumber}/export?raffle_id=${raffleId}&authorization=Bearer ${token}`, { responseType: 'blob' });
export const adminSetWinner = (data, token) => api.post(`/admin/winner/set?authorization=Bearer ${token}`, data);

// Chatbot GPT
export const sendChatMessage = (message, sessionId) => api.post('/chat', { message, session_id: sessionId });

export default api;
